// @ts-nocheck
/*
 * Port of components/partials/dashboard/tts/SsmlTextEditor.js (461 lines).
 * BYTE-IDENTICAL. Contenteditable-based SSML editor:
 *   - `SSML_TAG_TYPES`, `stripSsml`, `parseSsmlSegments` are re-imported
 *     from `./ssmlHelpers` (already ported in T3) — this file adds the
 *     UI-only bits (pillHtml, DOM ↔ SSML round-trip, caret tracking, and
 *     debounced auto-detect suggestions).
 *   - Toolbar wraps the current selection in a `<say-as>` pill.
 *   - Dotted-underline suggestions are surfaced by AUTODETECT_RULES on
 *     debounced input; clicking one promotes it into a pill.
 *   - `serializeToHtml` mounts the initial value; `domToSsmlString` reads
 *     back on each input to emit the canonical SSML for the parent form.
 * Loads SsmlTextEditor.scss (verbatim copy).
 */
import React, { useRef, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { SSML_TAG_TYPES, stripSsml } from './ssmlHelpers';
import './SsmlTextEditor.scss';

// Re-export the helpers so v1's `import SsmlTextEditor, { stripSsml,
// SSML_TAG_TYPES } from ".../SsmlTextEditor"` shape keeps working.
export { SSML_TAG_TYPES, stripSsml };

const TAG_EXAMPLES: any = {
  currency: {
    context: 'Your balance is 1000',
    sample: '1000',
    spoken: 'one thousand rupees',
  },
  // number: { context: "You have 25 messages", sample: "25", spoken: "twenty five" },
  digits: {
    context: 'Your OTP is 483920',
    sample: '483920',
    spoken: 'four-eight-three-nine-two-zero',
  },
  date: {
    context: 'Appointment on 25/12/2026',
    sample: '25/12/2026',
    spoken: '25th December 2026',
  },
  time: {
    context: 'Meeting at 5:30 PM',
    sample: '5:30 PM',
    spoken: 'five thirty PM',
  },
  telephone: {
    context: 'Call us at 9876543210',
    sample: '9876543210',
    spoken: 'number read out digit-group by digit-group',
  },
};

const AUTODETECT_RULES: any[] = [
  { interpretAs: 'currency', re: /(₹|\$|Rs\.?)\s?[\d,]+(\.\d{1,2})?/g },
  {
    interpretAs: 'date',
    re: /\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/g,
  },
  { interpretAs: 'time', re: /\b\d{1,2}:\d{2}\s?(am|pm|AM|PM)?\b/g },
  { interpretAs: 'telephone', re: /\b\d{10}\b/g },
  // { interpretAs: "ordinal", re: /\b\d+(st|nd|rd|th)\b/g },
  { interpretAs: 'digits', re: /(?<![\d/:-])\d{4,6}(?![\d/:-])/g },
];

const SAY_AS_RE =
  /<say-as\s+interpret-as="([^"]*)"\s+lang="([^"]*)"\s+rate="([^"]*)">([\s\S]*?)<\/say-as>/g;

function escapeHtml(str: any) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function textToHtml(text: any) {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

function pillHtml(interpretAs: string, lang: string, rate: string, innerText: string) {
  const type: any = SSML_TAG_TYPES.find((t: any) => t.interpretAs === interpretAs) || {};
  const {
    label = interpretAs,
    glyph = '',
    border = '#94a3b8',
    bg = '#f8fafc',
    fg = '#475569',
  } = type;
  const pillStyle =
    `display:inline-flex;align-items:center;gap:0.5em;padding:0.18em 0.9em 0.18em 0.12em;` +
    `margin:0 0.2em;border:1.5px solid ${border};border-radius:9px;background:${bg};color:${fg};` +
    `font-size:0.92em;line-height:1.6;vertical-align:middle;white-space:nowrap;`;
  const glyphStyle =
    `display:inline-flex;align-items:center;justify-content:center;border-radius:6px;` +
    `background:${fg};color:#fff;font-size:0.68em;font-weight:700;padding:0.1em 0.6em;`;
  const labelStyle = `font-size:0.62em;font-weight:700;letter-spacing:0.04em;opacity:0.85;`;
  const removeStyle = `cursor:pointer;font-weight:700;opacity:0.55;padding:0 0.15em;`;
  return (
    `<span class="ssml-pill" contenteditable="false" data-interpret-as="${escapeHtml(
      interpretAs,
    )}" data-lang="${escapeHtml(lang)}" data-rate="${escapeHtml(rate)}" style="${pillStyle}">` +
    `<span class="ssml-pill__glyph" style="${glyphStyle}">${escapeHtml(glyph)}</span>` +
    `<span class="ssml-pill__text" style="font-weight:700;">${escapeHtml(innerText)}</span>` +
    `<span class="ssml-pill__label" style="${labelStyle}">${escapeHtml(
      label.toUpperCase(),
    )}</span>` +
    `<span class="ssml-pill__remove" data-role="remove-pill" title="Remove tag" style="${removeStyle}">×</span>` +
    `</span>`
  );
}

function serializeToHtml(value: string) {
  if (!value) return '';
  let html = '';
  let lastIndex = 0;
  let match;
  SAY_AS_RE.lastIndex = 0;
  while ((match = SAY_AS_RE.exec(value))) {
    const [full, interpretAs, lang, rate, inner] = match;
    html += textToHtml(value.slice(lastIndex, match.index));
    html += pillHtml(interpretAs, lang, rate, inner);
    lastIndex = match.index + full.length;
  }
  html += textToHtml(value.slice(lastIndex));
  return html;
}

function nodeToPlain(node: any): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  if (node.classList && node.classList.contains('ssml-pill')) {
    const interpretAs = node.dataset.interpretAs || '';
    const lang = node.dataset.lang || '';
    const rate = node.dataset.rate || '';
    const textEl = node.querySelector('.ssml-pill__text');
    const inner = textEl ? textEl.textContent : '';
    return `<say-as interpret-as="${interpretAs}" lang="${lang}" rate="${rate}">${inner}</say-as>`;
  }
  if (node.classList && node.classList.contains('ssml-suggestion')) {
    return node.textContent;
  }
  if (node.nodeName === 'BR') return '\n';
  let out = '';
  node.childNodes.forEach((child: any) => {
    out += nodeToPlain(child);
  });
  return out;
}

function domToSsmlString(root: any) {
  let out = '';
  Array.from(root.childNodes).forEach((node: any, idx: number) => {
    const isBlock =
      node.nodeType === Node.ELEMENT_NODE &&
      (node.nodeName === 'DIV' || node.nodeName === 'P');
    if (isBlock && idx > 0) out += '\n';
    out += nodeToPlain(node);
  });
  return out;
}

function htmlToNode(html: string) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html.trim();
  return tpl.content.firstChild;
}

function closestPill(node: any) {
  let el = node && node.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  while (el) {
    if (el.classList && el.classList.contains('ssml-pill')) return el;
    el = el.parentElement;
  }
  return null;
}

function findSuggestions(text: string) {
  const matches: any[] = [];
  AUTODETECT_RULES.forEach(({ interpretAs, re }: any) => {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text))) {
      const start = m.index;
      const end = start + m[0].length;
      if (m[0].length === 0) {
        re.lastIndex++;
        continue;
      }
      const overlaps = matches.some((x: any) => start < x.end && end > x.start);
      if (!overlaps) matches.push({ start, end, interpretAs, text: m[0] });
    }
  });
  matches.sort((a: any, b: any) => a.start - b.start);
  return matches;
}

function applySuggestions(root: any) {
  root.querySelectorAll('.ssml-suggestion').forEach((el: any) => {
    el.replaceWith(document.createTextNode(el.textContent));
  });
  root.normalize();

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: any) {
      let p = node.parentElement;
      while (p && p !== root) {
        if (p.classList && p.classList.contains('ssml-pill'))
          return NodeFilter.FILTER_REJECT;
        p = p.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  } as any);

  const textNodes: any[] = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  let count = 0;
  textNodes.forEach((textNode: any) => {
    const text = textNode.textContent;
    const matches = findSuggestions(text);
    if (matches.length === 0) return;
    count += matches.length;
    const frag = document.createDocumentFragment();
    let last = 0;
    matches.forEach(({ start, end, interpretAs, text: matchedText }: any) => {
      if (start > last)
        frag.appendChild(document.createTextNode(text.slice(last, start)));
      const span = document.createElement('span');
      span.className = 'ssml-suggestion';
      span.dataset.suggestInterpretAs = interpretAs;
      span.textContent = matchedText;
      frag.appendChild(span);
      last = end;
    });
    if (last < text.length)
      frag.appendChild(document.createTextNode(text.slice(last)));
    textNode.replaceWith(frag);
  });
  return count;
}

function getCaretOffset(root: any) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.focusNode || !root.contains(sel.focusNode))
    return null;
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(sel.focusNode, sel.focusOffset);
  return range.toString().length;
}

function setCaretOffset(root: any, offset: any) {
  if (offset == null) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null as any);
  let remaining = offset;
  let node: any;
  let lastNode: any = null;
  while ((node = walker.nextNode())) {
    lastNode = node;
    const len = node.textContent.length;
    if (remaining <= len) {
      const range = document.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      const sel = window.getSelection()!;
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    remaining -= len;
  }
  const range = document.createRange();
  if (lastNode) range.setStart(lastNode, lastNode.textContent.length);
  else range.selectNodeContents(root);
  range.collapse(false);
  const sel = window.getSelection()!;
  sel.removeAllRanges();
  sel.addRange(range);
}

const SsmlTextEditor = ({
  value,
  onChange,
  lang = 'en',
  rate = 1.0,
  disabled = false,
  placeholder = '',
  className = '',
}: any) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastValueRef = useRef(value || '');
  const mountedRef = useRef(false);
  const debounceRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = serializeToHtml(value || '');
    lastValueRef.current = value || '';
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mountedRef.current || !ref.current) return;
    if ((value || '') !== lastValueRef.current) {
      ref.current.innerHTML = serializeToHtml(value || '');
      lastValueRef.current = value || '';
    }
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const scheduleAutoDetect = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!ref.current) return;
      const hasFocus = document.activeElement === ref.current;
      const caretOffset = hasFocus ? getCaretOffset(ref.current) : null;
      setSuggestionCount(applySuggestions(ref.current));
      if (hasFocus) setCaretOffset(ref.current, caretOffset);
    }, 500);
  };

  const handleInput = () => {
    if (!ref.current) return;
    if (
      !ref.current.querySelector('.ssml-pill') &&
      ref.current.textContent!.trim() === ''
    ) {
      ref.current.innerHTML = '';
    }
    const newVal = domToSsmlString(ref.current);
    lastValueRef.current = newVal;
    onChangeRef.current(newVal);
    scheduleAutoDetect();
  };

  const insertPill = (interpretAs: string, range: any, text: string) => {
    const type: any = SSML_TAG_TYPES.find((t: any) => t.interpretAs === interpretAs);
    const effectiveRate = type && type.rate != null ? type.rate : rate;
    const pillNode = htmlToNode(pillHtml(interpretAs, lang, String(effectiveRate), text));
    range.deleteContents();
    range.insertNode(pillNode);
    const sel = window.getSelection()!;
    sel.removeAllRanges();
    const r2 = document.createRange();
    r2.setStartAfter(pillNode as any);
    r2.collapse(true);
    sel.addRange(r2);
    handleInput();
  };

  const handleToolbarClick = (type: any) => {
    if (disabled || !ref.current) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      toast.warning('Select text first, then apply a tag.');
      return;
    }
    const range = sel.getRangeAt(0);
    if (!ref.current.contains(range.commonAncestorContainer)) {
      toast.warning('Select text first, then apply a tag.');
      return;
    }
    const text = range.toString();
    if (!text.trim()) return;
    if (closestPill(range.startContainer) || closestPill(range.endContainer)) {
      toast.warning('This text is already tagged. Remove it first.');
      return;
    }
    insertPill(type.interpretAs, range, text);
  };

  const handleEditorClick = (e: any) => {
    const suggestion =
      e.target.closest && e.target.closest('.ssml-suggestion');
    if (suggestion && ref.current!.contains(suggestion)) {
      e.preventDefault();
      const interpretAs = suggestion.dataset.suggestInterpretAs;
      const range = document.createRange();
      range.selectNode(suggestion);
      insertPill(interpretAs, range, suggestion.textContent);
      return;
    }
    const removeBtn =
      e.target.closest && e.target.closest('[data-role="remove-pill"]');
    if (removeBtn) {
      const pill = removeBtn.closest('.ssml-pill');
      if (pill) {
        const textEl = pill.querySelector('.ssml-pill__text');
        pill.replaceWith(
          document.createTextNode(textEl ? textEl.textContent : ''),
        );
        handleInput();
      }
    }
  };

  const handlePaste = (e: any) => {
    e.preventDefault();
    const text = (e.clipboardData || (window as any).clipboardData).getData(
      'text/plain',
    );
    if ((document as any).execCommand)
      (document as any).execCommand('insertText', false, text);
  };

  return (
    <div
      className={`ssml-editor ${className || ''} ${
        disabled ? 'ssml-editor--disabled' : ''
      }`}
    >
      <div className="ssml-toolbar">
        <span className="ssml-toolbar__label">Read selection as:</span>
        <div className="ssml-toolbar__buttons">
          {SSML_TAG_TYPES.map((t: any) => (
            <button
              key={t.key}
              type="button"
              className="ssml-toolbar__btn"
              style={{
                borderColor: t.border,
                background: t.bg,
                color: t.fg,
                padding: '0.36rem 1rem',
                gap: '0.45rem',
              }}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleToolbarClick(t)}
            >
              <span
                className="ssml-toolbar__glyph"
                style={{ background: t.fg }}
              >
                {t.glyph}
              </span>
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ssml-help-toggle"
          title="How do I use these?"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowHelp((v) => !v)}
        >
          ?
        </button>
      </div>

      {showHelp && (
        <div className="ssml-help-panel">
          <div className="ssml-help-panel__steps">
            <strong>How to use:</strong> First type the text as you want it spoken
            (e.g. "Your balance is 1000") → select that part (e.g. "1000") → click
            the matching button above. Or just type a number/date/time/phone as-is
            — the editor shows a dotted-underline suggestion, click it to confirm.
          </div>
          <div className="ssml-help-panel__grid">
            {SSML_TAG_TYPES.map((t: any) => {
              const ex = TAG_EXAMPLES[t.interpretAs];
              if (!ex) return null;
              return (
                <div key={t.key} className="ssml-help-panel__row">
                  <span
                    className="ssml-help-panel__tag"
                    style={{
                      borderColor: t.border,
                      background: t.bg,
                      color: t.fg,
                    }}
                  >
                    <span
                      className="ssml-toolbar__glyph"
                      style={{ background: t.fg }}
                    >
                      {t.glyph}
                    </span>
                    {t.label}
                  </span>
                  <span className="ssml-help-panel__example">
                    Type: <code>{ex.context}</code> → select{' '}
                    <code>{ex.sample}</code>
                  </span>
                  <span className="ssml-help-panel__spoken">
                    Speaks: “{ex.spoken}”
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        ref={ref}
        className="ssml-editor__field"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onClick={handleEditorClick}
        onPaste={handlePaste}
        data-placeholder={placeholder}
      />

      <div className="ssml-editor__footer">
        <span className="ssml-editor__hint">
          {suggestionCount > 0 && (
            <span className="ssml-editor__autodetect">
              {suggestionCount} auto-detected — click to confirm
            </span>
          )}
        </span>
        <span className="ssml-editor__count">
          {stripSsml(value).length} character
          {stripSsml(value).length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default SsmlTextEditor;
